import { useApiFilters, useFiltersV2MetaAutoUpdate } from "PFApp/components/filters_v2";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useActivityRankedShortlists } from "PFCore/hooks/queries/shortlists/use_activity_ranked_shortlists";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { MatchScores, Shortlist } from "PFTypes";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export type ShortlistWithScores = Omit<Shortlist, "scores"> & {
  scores: MatchScores | null;
};

type UseShortlistItemsReturn = {
  tableData: ShortlistWithScores[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
};

export const useShortlistItems = ({ activityId }: { activityId: number }): UseShortlistItemsReturn => {
  const { t } = useTranslation();
  const { showGrowl } = useGrowlStoreActions();
  const { getFirstError } = useResponseErrors();

  const { apiFilters } = useApiFilters({ sendDefaultsAsUndefined: false, sendEmptyAsUndefined: false });

  const {
    data: shortlists,
    isLoading: isShortlistsLoading,
    isFetching: isShortlistsFetching,
    error: shortlistsError,
    isError: isShortlistsError
  } = useActivityShortlists(activityId, { filters: apiFilters });
  useFiltersV2MetaAutoUpdate(shortlists?.meta);

  const {
    data: rankedProfiles,
    isLoading: isRankedProfilesLoading,
    error: rankedProfilesError,
    isError: isRankedProfilesError
  } = useActivityRankedShortlists({
    activityId
  });

  useEffect(() => {
    if (shortlistsError) {
      const errorMessage = getFirstError(shortlistsError);
      showGrowl({
        message: errorMessage || t("somethingWentWrong"),
        kind: "error"
      });
    }
    if (rankedProfilesError) {
      const errorMessage = getFirstError(rankedProfilesError);
      showGrowl({
        message: errorMessage || t("somethingWentWrong"),
        kind: "error"
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlistsError, rankedProfilesError]);

  const isLoading = isShortlistsLoading || isRankedProfilesLoading;
  const isFetching = isShortlistsFetching;
  const isError = isShortlistsError || isRankedProfilesError;

  const scoresMap = new Map((rankedProfiles?.entries ?? []).map((entry) => [entry.id, entry.scores]));

  const tableData: ShortlistWithScores[] = (shortlists?.entries ?? []).map((shortlist) => ({
    ...shortlist,
    scores: scoresMap.get(shortlist.profile.id) ?? null
  }));

  return { tableData, isLoading, isFetching, isError };
};
